// Help Genie brand tokens — TypeScript mirror of
// `scripts/collateral/lib/brand.mjs`.
//
// One source of truth for in-app components that need the same palette, fonts,
// or copy rules used by the collateral PDF pipeline (pitch decks, brochure,
// infographics, getting-started, how-to one-pagers). Keep these two files in
// sync — if you change a colour here, update brand.mjs too (and vice versa).
//
// Canonical: Spark Blue / Magenta Beam / Amber Glow on Poppins + Baloo Paaji 2.

export const palette = {
  // Signature brand stops
  sparkBlue: '#4E9CFF',
  magentaBeam: '#FF4DB8',
  amberGlow: '#FFBB33',
  logoNavy: '#28406F',

  // Text + surface system
  navy: '#28406F',
  navyDeep: '#1A2C4F',
  bodyFg: '#222B45',
  mutedFg: '#6B7280',
  border: '#E5EAF0',
  borderLight: '#EEF1F6',
  pageBg: '#FFFFFF',
  pageTint: '#F9FAFC',
  rowAlt: '#F4F6FB',
  white: '#FFFFFF',

  // Signature gradients (matches brand.mjs)
  gradientCss: 'linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)',
  gradientCssWide: 'linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)',
  gradientCssVertical: 'linear-gradient(180deg, #4E9CFF 0%, #FF4DB8 100%)',
  gradientCssDiagonal: 'linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 60%, #FFBB33 100%)',
  gradientCssWarm: 'linear-gradient(135deg, #FF4DB8 0%, #FFBB33 100%)',
  gradientCssCool: 'linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)',
} as const;

export const font = {
  sans: `'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  display: `'Baloo Paaji 2', 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif`,
  mono: `'JetBrains Mono', Menlo, Consolas, monospace`,
} as const;

export const copyRules = {
  displayName: 'Help Genie',
  never: ['AI receptionist', '—'] as const,
  demoPhone: '+64 9 886 4856',
  demoWeb: 'https://helpgenie.ai/live-agent/business-onboarding',
  domain: 'helpgenie.ai',
} as const;

// Stable asset paths — these resolve under public/ at runtime so they work
// from anywhere in the app via root-relative URLs.
export const brandAssets = {
  wordmark: '/branding/helpgenie/helpgenie-wordmark.png',
  icon: '/branding/helpgenie/helpgenie-icon.png',
  iconSquare: '/branding/helpgenie/helpgenie-icon-square.png',
  mascot: '/branding/helpgenie/mascot.png',
  mascotLarge: '/branding/helpgenie/mascot-large.png',
  iconsDir: '/branding/helpgenie/icons',
} as const;

// Generated collateral artifacts — every piece is committed alongside its
// HTML so the source is inspectable and the PDF is downloadable.
export const collateral = {
  brochure: {
    pdf: '/collateral/brochure/help-genie-brochure.pdf',
    html: '/collateral/brochure/help-genie-brochure.html',
  },
  deck: {
    pdf: '/collateral/deck/pitch-deck.pdf',
    html: '/collateral/deck/pitch-deck.html',
  },
  deckPortrait: {
    pdf: '/collateral/deck-portrait/pitch-deck-portrait.pdf',
    html: '/collateral/deck-portrait/pitch-deck-portrait.html',
  },
  deckWidescreen: {
    pdf: '/collateral/pitch-deck-widescreen/deck.pdf',
    pptx: '/collateral/pitch-deck-widescreen/deck.pptx',
    html: '/collateral/pitch-deck-widescreen/deck.html',
  },
  noBsDeck: {
    pdf: '/collateral/no-bs-deck/deck.pdf',
    pptx: '/collateral/no-bs-deck/deck.pptx',
    html: '/collateral/no-bs-deck/deck.html',
  },
  capabilityInfographic: {
    pdf: '/collateral/infographics/what-you-can-do/index.pdf',
    html: '/collateral/infographics/what-you-can-do/index.html',
  },
  howTo: {
    buildYourFirstGenie: {
      pdf: '/collateral/infographics/how-to/build-your-first-genie/index.pdf',
      html: '/collateral/infographics/how-to/build-your-first-genie/index.html',
    },
    linkAKnowledgeSource: {
      pdf: '/collateral/infographics/how-to/link-a-knowledge-source/index.pdf',
      html: '/collateral/infographics/how-to/link-a-knowledge-source/index.html',
    },
    shareYourQrAndPhone: {
      pdf: '/collateral/infographics/how-to/share-your-qr-and-phone/index.pdf',
      html: '/collateral/infographics/how-to/share-your-qr-and-phone/index.html',
    },
  },
  gettingStarted: {
    pdf: '/collateral/platform/getting-started/index.pdf',
    html: '/collateral/platform/getting-started/index.html',
  },
  gettingStartedV2: {
    pdf: '/collateral/platform/getting-started-v2/index.pdf',
    html: '/collateral/platform/getting-started-v2/index.html',
  },
} as const;

export type CollateralPiece = keyof typeof collateral;
